import * as React from 'react';
import { Platform } from 'react-native';
import RNMapView from 'react-native-maps'; // resolves to react-native-web-maps on web; see webpack config for aliasing

import { log } from '../../utils/logger';
import { MapViewMobile, MapViewMobileRegion } from './MapViewMobile';
import { MapViewWeb, MapViewWebRegion } from './MapViewWeb';

/**
 * MapView attempts to provide provides a consistent abstraction of the platform-specific mobile (React Native) and web
 * (react-native-web-maps) implementations.  In particular, the distinction between deltas (React Native) and zoom level
 * (web) is maintained within a MapViewRegion that should always be treated as opaque.
 */
export function MapView(props) {
  const { style, initialRegion, mapViewRef, onRegionChangeComplete, onPress, children } = props;

  const mapViewImplRef = React.useRef(null);

  React.useImperativeHandle(
    mapViewRef,
    () => {
      // Wrap the MapView that is passed in as a ref to constrain the interface available to the parent container to the
      // platform-agnositc abstraction that this component provides.  Because of the differences between the React
      // Native and web implementations, this is intentionally a minimal interface of only what is absolutely needed.
      //
      // https://react.dev/reference/react/useImperativeHandle
      return {
        animateToRegion(mapViewRegion) {
          mapViewImplRef.current?.animateToRegion(mapViewRegion);
        },
      };
    },
    []
  );

  if (Platform.OS === 'web') {
    return (
      <MapViewWeb
        initialRegion={initialRegion}
        mapViewRef={mapViewImplRef}
        mapViewImpl={RNMapView}
        onRegionChangeComplete={onRegionChangeComplete}
        onPress={onPress}
        style={style}
      >
        {children}
      </MapViewWeb>
    );
  } else {
    return (
      <MapViewMobile
        initialRegion={initialRegion}
        mapViewRef={mapViewImplRef}
        mapViewImpl={RNMapView}
        onRegionChangeComplete={onRegionChangeComplete}
        onPress={onPress}
        style={style}
      >
        {children}
      </MapViewMobile>
    );
  }
}

export function makeMapViewRegion({ latitude, latitudeDelta, longitude, longitudeDelta }) {
  let mapViewRegion;
  if (Platform.OS === 'web') {
    mapViewRegion = new MapViewWebRegion(latitude, latitudeDelta, longitude, longitudeDelta);
  } else {
    mapViewRegion = new MapViewMobileRegion(latitude, latitudeDelta, longitude, longitudeDelta);
  }

  log('MapView.makeMapViewRegion()', { mapViewRegion, asNativeRegion: mapViewRegion.asNativeRegion() });

  return mapViewRegion;
}
